import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { authApi } from '../api/auth';
import type { AuthUser } from '../types/contracts';

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  authSource: 'tma' | 'web' | 'dev' | null;
  refresh: () => Promise<void>;
  onLoggedIn: (user: AuthUser) => void;
  logout: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [authSource, setAuthSource] = useState<'tma' | 'web' | 'dev' | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const me = await authApi.me();
      setUser(me.profile);
      setAuthSource(me.authSource);
    } catch {
      setUser(null);
      setAuthSource(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const onLoggedIn = useCallback((u: AuthUser) => {
    setUser(u);
    setAuthSource('web');
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
    setAuthSource(null);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const handler = () => {
      setUser(null);
      setAuthSource(null);
    };
    window.addEventListener('apw:unauthorized', handler);
    return () => window.removeEventListener('apw:unauthorized', handler);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, loading, authSource, refresh, onLoggedIn, logout }),
    [user, loading, authSource, refresh, onLoggedIn, logout],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
